import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../api';
import {
  ArrowLeft,
  User as UserIcon,
  Mail,
  Lock,
  Shield,
  ShieldCheck,
  Eye,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import clsx from 'clsx';

const ROLES = [
  { 
    value: 'admin', 
    label: 'Admin', 
    icon: ShieldCheck,
    description: 'Accesso completo a tutte le funzionalità',
    color: 'border-purple-200 bg-purple-50',
    selectedColor: 'border-purple-500 bg-purple-100 ring-2 ring-purple-500',
  },
  { 
    value: 'editor', 
    label: 'Editor', 
    icon: Shield,
    description: 'Può creare e modificare progetti e clienti',
    color: 'border-blue-200 bg-blue-50',
    selectedColor: 'border-blue-500 bg-blue-100 ring-2 ring-blue-500',
  },
  { 
    value: 'viewer', 
    label: 'Viewer', 
    icon: Eye,
    description: 'Può solo visualizzare progetti e clienti',
    color: 'border-gray-200 bg-gray-50',
    selectedColor: 'border-gray-500 bg-gray-100 ring-2 ring-gray-500',
  },
];

interface FormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: 'admin' | 'editor' | 'viewer';
  active: boolean;
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  role?: string;
}

export function UserFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'viewer',
    active: true,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);

  // Carica utente esistente
  const { data: userData, isLoading: isLoadingUser } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersApi.get(id!),
    enabled: isEdit,
  });

  // Popola form con dati esistenti
  useEffect(() => {
    if (userData?.data?.data?.user) {
      const user = userData.data.data.user;
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        confirmPassword: '',
        role: user.role,
        active: user.active,
      });
    }
  }, [userData]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: { name: string; email: string; password: string; role: string }) =>
      usersApi.create(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate('/users');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<{ name: string; role: string; active: boolean }>) =>
      usersApi.update(id!, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      navigate('/users');
    },
  });

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Il nome è obbligatorio';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email è obbligatoria';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email non valida';
    }

    if (!isEdit) {
      if (!formData.password) {
        newErrors.password = 'La password è obbligatoria';
      } else if (formData.password.length < 8) {
        newErrors.password = 'La password deve essere di almeno 8 caratteri';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Le password non corrispondono';
      }
    }

    if (!formData.role) {
      newErrors.role = 'Seleziona un ruolo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;

    if (isEdit) {
      updateMutation.mutate({
        name: formData.name,
        role: formData.role,
        active: formData.active,
      });
    } else {
      createMutation.mutate({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error || updateMutation.error;

  if (isEdit && isLoadingUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/users')}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna agli utenti
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Modifica utente' : 'Nuovo utente'}
        </h1>
        <p className="text-gray-600">
          {isEdit 
            ? 'Modifica le informazioni dell\'utente'
            : 'Crea un nuovo utente per accedere al sistema'
          }
        </p>
      </div>

      {/* Error message */}
      {mutationError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Errore</p>
            <p className="text-sm text-red-600">{mutationError.message}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info */}
        <div className="card p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Informazioni base</h2>
          
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome completo *
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Mario Rossi"
                  className={clsx('input pl-10', errors.name && 'border-red-300')}
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="mario.rossi@email.com"
                  disabled={isEdit}
                  className={clsx(
                    'input pl-10', 
                    errors.email && 'border-red-300',
                    isEdit && 'bg-gray-50 cursor-not-allowed'
                  )}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
              {isEdit && (
                <p className="mt-1 text-xs text-gray-500">L'email non può essere modificata</p>
              )}
            </div>
          </div>
        </div>

        {/* Password (only for new users) */}
        {!isEdit && (
          <div className="card p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Password</h2>
            
            <div className="space-y-4">
              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Almeno 8 caratteri"
                    className={clsx('input pl-10 pr-10', errors.password && 'border-red-300')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conferma password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Ripeti la password"
                    className={clsx('input pl-10', errors.confirmPassword && 'border-red-300')}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Role */}
        <div className="card p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Ruolo</h2>
          
          <div className="space-y-3">
            {ROLES.map((role) => {
              const RoleIcon = role.icon;
              const isSelected = formData.role === role.value;
              
              return (
                <label
                  key={role.value}
                  className={clsx(
                    'flex items-start p-4 border rounded-lg cursor-pointer transition-all',
                    isSelected ? role.selectedColor : role.color + ' hover:border-gray-300'
                  )}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role.value}
                    checked={isSelected}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                    className="sr-only"
                  />
                  <div className="flex items-center space-x-3 flex-1">
                    <RoleIcon className={clsx(
                      'w-5 h-5',
                      isSelected ? 'text-gray-900' : 'text-gray-500'
                    )} />
                    <div>
                      <p className={clsx(
                        'font-medium',
                        isSelected ? 'text-gray-900' : 'text-gray-700'
                      )}>
                        {role.label}
                      </p>
                      <p className="text-sm text-gray-500">{role.description}</p>
                    </div>
                  </div>
                  {isSelected && (
                    <CheckCircle className="w-5 h-5 text-primary-600" />
                  )}
                </label>
              );
            })}
          </div>
          {errors.role && (
            <p className="mt-2 text-sm text-red-600">{errors.role}</p>
          )}
        </div>

        {/* Status (only for edit) */}
        {isEdit && (
          <div className="card p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Stato</h2>
            
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-gray-700">Utente attivo</span>
            </label>
            <p className="mt-1 text-sm text-gray-500 ml-6">
              Gli utenti disattivati non possono accedere al sistema
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/users')}
            className="btn-secondary"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="btn-primary"
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEdit ? 'Salvataggio...' : 'Creazione...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEdit ? 'Salva modifiche' : 'Crea utente'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
